import type {
  ProcessDraftAssignment,
  ProcessDraftEventNode,
  ProcessDraftStep
} from '@bliks-io/contracts';

/**
 * The three collections a draft-graph node can live in
 * (`GetProcessDraftResponse.steps[]` / `.draftAssignments[]` / `.events[]`).
 */
export type DraftNodeKind = 'step' | 'assignment' | 'event';

/** Any document that can sit in one of the three draft cache arrays. */
export type DraftGraphMember = ProcessDraftStep | ProcessDraftAssignment | ProcessDraftEventNode;

/**
 * A located draft-graph node: the cache document plus the collection it was
 * found in. The `kind` discriminator is derived from the collection — cache
 * rows themselves don't reliably carry a `nodeKind` at runtime (assignments
 * never do; steps only when the backend stamped it).
 */
export type DraftGraphNode =
  | { kind: 'step'; node: ProcessDraftStep }
  | { kind: 'assignment'; node: ProcessDraftAssignment }
  | { kind: 'event'; node: ProcessDraftEventNode };

/**
 * Resolved identity of a draft-graph node across the two id spaces:
 * - `stableId` — the graph-topology id (`stableStepId`), what React Flow node
 *   ids, edge endpoints, and `inputs[].sourceSteps[].stepId` use.
 * - `backendId` — the id the persistence routes require: the document `_id`
 *   for steps and assignments, the public event id (= `stableStepId`) for
 *   event rows (their storage `_id` is draft-internal and never routed by).
 */
export interface DraftNodeRef {
  kind: DraftNodeKind;
  backendId: string;
  stableId: string;
}

/**
 * Thrown by `resolveNodeRef` when the node is missing from every collection
 * or was found under a kind the caller didn't expect. Callers that can treat
 * a stale id as a no-op catch this; everyone else fails fast instead of
 * sending a maybe-wrong id to the backend (the invalidate-on-settle backstop
 * then self-heals the cache).
 */
export class DraftNodeNotFoundError extends Error {
  readonly nodeId: string;
  readonly expectedKind?: DraftNodeKind | readonly DraftNodeKind[];
  readonly foundKind?: DraftNodeKind;

  constructor(
    nodeId: string,
    expectedKind?: DraftNodeKind | readonly DraftNodeKind[],
    foundKind?: DraftNodeKind
  ) {
    const expected = expectedKind
      ? ` (expected ${Array.isArray(expectedKind) ? expectedKind.join('|') : String(expectedKind)})`
      : '';
    super(
      foundKind
        ? `Draft node ${nodeId} is a ${foundKind}${expected}`
        : `Draft node ${nodeId} not found in draft cache${expected}`
    );
    this.name = 'DraftNodeNotFoundError';
    this.nodeId = nodeId;
    this.expectedKind = expectedKind;
    this.foundKind = foundKind;
  }
}
